import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  CalendarClock,
  LayoutDashboard,
  ScrollText,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';

import { useSession } from '@/components/auth/session';
import { RepoDot } from '@/components/common/bits';
import { ClaudeMark } from '@/components/common/marks';
import { AccountMenu } from '@/components/layout/AccountMenu';
import { LiveDot } from '@/components/layout/LiveDot';
import { useShell } from '@/components/layout/shell';
import { Button } from '@/components/ui/button';
import type { LiveState } from '@/hooks/use-activity-feed';
import { useBriefs, useProjects, useReview, useSchedules, useWorkers } from '@/hooks/use-fleet';
import { openBriefs } from '@/lib/briefs';
import { gateCounts } from '@/lib/fleet';
import { pausedForViewer } from '@/lib/schedules';
import { cn } from '@/lib/utils';

/**
 * One front door, three places behind it, and an admin section of one row.
 *
 * The rail has been cut twice. The first pass took six nouns (Inbox, Tasks,
 * Briefs, Review, Fleet, People) down to four destinations plus three admin
 * links, because six were naming three ideas. This pass takes the four down to
 * one plus a group, because four peers is still four decisions before a person
 * can look at anything, and three of the four existed to report a number.
 *
 * Now:
 *
 * - **Dashboard** is the front page and the only row that carries the accent.
 *   It answers what is waiting on you, and it carries the figure each of the
 *   three rows below it was being opened for. See `routes/home.tsx`.
 * - **Work** is where those three still live: **Jobs** (what people opened,
 *   with the task board reachable from it as a view rather than a nav row of
 *   its own -- issue #60 folded the old, separate "Steps" entry in here),
 *   **Schedules** and **Agents** (the machines, not admin-only, because a
 *   member has devices too). They are under a heading rather than beside
 *   Dashboard, which is the whole difference between a front door and a fork
 *   in the road.
 * - **Projects** is the repos, unchanged in spirit.
 * - **Admin** is one row. It was three -- Overview, People, Audit log -- which
 *   is a table of contents in a navigation rail. The section carries its own
 *   tabs now, on the page, beside the figures they filter.
 *
 * Two things stay deliberately as they were. Colour still means one thing here:
 * a count in the accent is a person being waited on, and nothing else in the
 * rail is coloured. And Agents wears Claude's own mark rather than a generic
 * chip outline, because what runs there is Claude Code on somebody's laptop and
 * the icon may as well say so.
 */
interface NavEntry {
  to: string;
  label: string;
  /** Lucide's icons and Claude's own mark both satisfy this. */
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  count?: number;
  /** Draw the count in the accent: something is waiting on a person. */
  gate?: boolean;
  exact?: boolean;
}

function NavRow({ entry, onNavigate }: { entry: NavEntry; onNavigate?: () => void }) {
  const Icon = entry.icon;
  return (
    <Link
      to={entry.to}
      onClick={onNavigate}
      activeOptions={{ exact: entry.exact ?? false }}
      className={cn(
        'group text-fg-2 hover:bg-raised hover:text-foreground relative',
        'flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-base transition-colors duration-100',
      )}
      activeProps={{ className: 'bg-raised text-foreground font-medium' }}
    >
      <Icon className="size-4 shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
      {entry.count ? (
        <span
          className={cn(
            'tnum shrink-0 text-xs',
            entry.gate
              ? 'text-on-accent rounded-full px-1.5 py-px font-semibold'
              : 'text-fg-3 font-medium',
          )}
          style={entry.gate ? { background: 'var(--dy-grad-gate)' } : undefined}
        >
          {entry.count}
        </span>
      ) : null}
    </Link>
  );
}

function SectionLabel({ children }: { children: string }) {
  return <div className="dy-eyebrow px-2.5 pt-4 pb-1.5">{children}</div>;
}

export function SidebarBody({ live, onNavigate }: { live: LiveState; onNavigate?: () => void }) {
  const { session } = useSession();
  const { openPalette, chordArmed } = useShell();
  // On a temporary password the hub refuses every one of these, so the rail
  // asks for none of them: the counts stay blank rather than failing in turn.
  const unlocked = !session.mustChangePassword;
  const briefs = useBriefs(unlocked);
  const review = useReview(unlocked);
  const workers = useWorkers(session.isAdmin && unlocked);
  const projects = useProjects(unlocked);
  const schedules = useSchedules(unlocked);
  const routerState = useRouterState({ select: (state) => state.location.pathname });

  const briefRows = briefs.data ?? [];
  // Both counts off one derivation: the gate total and the Jobs row used to be
  // computed two different ways and could disagree for a poll cycle.
  const gates = gateCounts(briefRows, review.data?.queue);

  // The front door, and the three places it opens into. They were four peers in
  // the old rail; see `routes/home.tsx` for why that was three destinations too
  // many. The count stays on Dashboard because the count is the reason to go
  // there, and it is the only accent in the rail.
  const front: NavEntry[] = [
    {
      to: '/',
      label: 'Dashboard',
      icon: LayoutDashboard,
      count: gates.total,
      gate: true,
      exact: true,
    },
    // No count: the page is a window, not a queue, and nothing on it waits on you.
    { to: '/metrics', label: 'Metrics', icon: BarChart3 },
  ];

  // A schedule paused after two bad runs is waiting on the person who approved
  // it, so it counts in the accent like a gate; a healthy schedule is not a
  // number anybody needs in the rail. Somebody else's paused schedule is not a
  // gate this person can pass, and every verb on it would answer them 403.
  const pausedSchedules = pausedForViewer(
    schedules.data ?? [],
    session.actor,
    session.isAdmin,
  ).length;

  const work: NavEntry[] = [
    { to: '/jobs', label: 'Jobs', icon: ScrollText, count: openBriefs(briefRows).length },
    {
      to: '/schedules',
      label: 'Schedules',
      icon: CalendarClock,
      count: pausedSchedules,
      gate: pausedSchedules > 0,
    },
    { to: '/agents', label: 'Agents', icon: ClaudeMark, count: workers.data?.length },
  ];

  const repos = (projects.data ?? []).slice(0, 6);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-16 shrink-0 items-center gap-2 px-3.5">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex min-w-0 items-center gap-2"
          aria-label="Dyspatch home"
        >
          <span
            className="size-[18px] shrink-0 rounded-[5px]"
            style={{ background: 'var(--dy-grad)' }}
            aria-hidden
          />
          <span className="dy-grad-text truncate text-lg font-bold tracking-[-0.025em]">
            Dyspatch
          </span>
        </Link>
        {/* The `g` chord, while it is armed. A mode with no indicator is a mode
            a person finds out about by pressing the wrong key. */}
        {chordArmed ? <kbd className="dy-kbd ml-1">g…</kbd> : null}
        <div className="ml-auto">
          <LiveDot state={live} />
        </div>
      </div>

      <div className="px-3 pb-2.5">
        <button
          type="button"
          onClick={openPalette}
          className={cn(
            'dy-inset text-fg-3 hover:text-fg-2 hover:bg-raised',
            'flex h-9 w-full items-center gap-2.5 px-2.5 text-base transition-colors duration-100',
          )}
        >
          <Search className="size-4 shrink-0" aria-hidden />
          <span className="flex-1 text-left">Search or jump to…</span>
          {/* Below `lg` this rail is the phone's nav sheet, and a phone has no
              cmd key. The button still opens the palette; only the promise of a
              keystroke goes away. */}
          <kbd className="dy-kbd max-lg:hidden">⌘K</kbd>
        </button>
      </div>

      <nav aria-label="Sections" className="dy-scroll min-h-0 flex-1 overflow-y-auto px-2.5 pb-2">
        <div className="space-y-0.5">
          {front.map((entry) => (
            <NavRow key={entry.to} entry={entry} onNavigate={onNavigate} />
          ))}
        </div>

        <SectionLabel>Work</SectionLabel>
        <div className="space-y-0.5">
          {work.map((entry) => (
            <NavRow key={entry.to} entry={entry} onNavigate={onNavigate} />
          ))}
        </div>

        {repos.length ? (
          <>
            <SectionLabel>Projects</SectionLabel>
            <div className="space-y-0.5">
              {repos.map((project) => (
                <Link
                  key={project.repo}
                  to="/project/$repo"
                  params={{ repo: project.repo }}
                  onClick={onNavigate}
                  className={cn(
                    'text-fg-2 hover:bg-raised hover:text-foreground',
                    'flex h-8 items-center gap-2.5 rounded-lg px-2.5 text-base transition-colors duration-100',
                    routerState === `/project/${project.repo}` &&
                      'bg-raised text-foreground font-medium',
                  )}
                >
                  <span className="grid size-4 shrink-0 place-items-center" aria-hidden>
                    <RepoDot repo={project.repo} className="size-2" />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{project.repo}</span>
                  {project.inReview ? (
                    <span className="text-gate tnum shrink-0 text-xs font-semibold">
                      {project.inReview}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          </>
        ) : null}

        {session.isAdmin ? (
          <>
            <SectionLabel>Admin</SectionLabel>
            <div className="space-y-0.5">
              {/* One row where there were three. Overview, People and Audit log
                  were three links to three pages of one section, which is a
                  table of contents in a navigation rail; the section has its own
                  tabs now and they live on the page, next to the figures they
                  filter. `exact` is off so the row stays lit on every page
                  inside it. */}
              <NavRow
                entry={{ to: '/admin', label: 'Admin', icon: ShieldCheck }}
                onNavigate={onNavigate}
              />
            </div>
          </>
        ) : null}
      </nav>

      <AccountMenu onNavigate={onNavigate} />
    </div>
  );
}

/** The rail on a laptop, and the slide-in sheet on a phone. */
export function Sidebar({ live }: { live: LiveState }) {
  const { navOpen, setNavOpen } = useShell();
  return (
    <>
      <aside className="dy-rail border-line fixed inset-y-0 left-0 z-30 hidden w-[248px] border-r lg:block">
        <SidebarBody live={live} />
      </aside>

      {navOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            className="dy-scrim-in absolute inset-0"
            style={{ background: 'var(--dy-overlay)' }}
          />
          <aside className="dy-rail dy-pop-in absolute inset-y-0 left-0 w-[276px] shadow-e3">
            <SidebarBody live={live} onNavigate={() => setNavOpen(false)} />
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute top-3 right-3"
              onClick={() => setNavOpen(false)}
              aria-label="Close navigation"
            >
              <X />
            </Button>
          </aside>
        </div>
      ) : null}
    </>
  );
}
